import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  DeviceId,
  Manufacturer,
} from '@service-initiation/shared-clients-tech-care-services';
import {
  useAnalytics,
  useInViewAnalytics,
} from '@service-initiation/shared-data-access-analytics';
import { useGoToNextStep } from '@service-initiation/shared-data-access-flow-manager';
import {
  LeftMainContentSection,
  MainContentContainer,
  PageContainer,
} from '@service-initiation/shared-ui-page-layout';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import {
  deviceTypePageConfirmedAtom,
  store,
  useAvailableDeviceTypesAsyncAtom,
  useDeviceDetailsAtom,
  useDeviceTypeAtom,
} from '@service-initiation/tech-care-data-access-store';
import {
  type DeviceDetails,
  deviceIdToImage,
} from '@service-initiation/tech-care-data-access-tech-care-services';
import { StepTitle } from '@service-initiation/tech-care-ui-step-title';

import { DeviceTypeCardGrid } from './components/DeviceTypeCardGrid';
import { cleanTicks } from './utils';

const Container = styled.section``;

const TEXTS = TranslationEntries.Repairs.DeviceType;

const OTHER_SELECTED_DEVICE_DETAILS: DeviceDetails = {
  children: [],
  hasChildren: false,
  id: DeviceId.OtherChild,
  imageLink: deviceIdToImage[DeviceId.OtherChild],
  isBottomLevel: true,
  manufacturer: Manufacturer.Other,
  name: 'Other',
  rootParentDeviceTypeId: DeviceId.Other,
  sortOrder: 0,
};
export const DeviceTypePage: FC = () => {
  const { t } = useTranslation();

  const [{ loading: loadingNextStep }, goToNextStep] = useGoToNextStep();
  const { ref: elementRef } = useInViewAnalytics('DeviceType');
  const { dispatchClickEvent } = useAnalytics();
  const { loading: loadingAvailableDeviceTypes, value: availableDeviceTypes } =
    useAvailableDeviceTypesAsyncAtom();
  const [, setDeviceType] = useDeviceTypeAtom();
  const [, setDeviceDetails] = useDeviceDetailsAtom();
  const handleDeviceCardClick = useCallback(
    (selectedDeviceId: number, deviceName: string) => {
      dispatchClickEvent('SelectDeviceType', {
        Cta: cleanTicks(deviceName),
      });

      setDeviceDetails(
        selectedDeviceId === DeviceId.Other
          ? OTHER_SELECTED_DEVICE_DETAILS
          : undefined
      );
      setDeviceType({ id: selectedDeviceId, name: deviceName });

      store.set(deviceTypePageConfirmedAtom, true);
      goToNextStep();
    },
    [dispatchClickEvent, goToNextStep, setDeviceDetails, setDeviceType]
  );

  return (
    <PageContainer>
      <MainContentContainer>
        <LeftMainContentSection>
          <Container data-testid="device-type" ref={elementRef}>
            <StepTitle subtitle={t(TEXTS.SubTitle)} title={t(TEXTS.Title)} />
            <DeviceTypeCardGrid
              deviceTypes={availableDeviceTypes || []}
              handleDeviceCardClick={handleDeviceCardClick}
              loading={loadingAvailableDeviceTypes || loadingNextStep}
            />
          </Container>
        </LeftMainContentSection>
      </MainContentContainer>
    </PageContainer>
  );
};
